/**
 * Appointments API - Schedule and manage appointments
 */
import { apiClient } from '../api-client';

// ==================== Types ====================

// ✅ FIXED FOREVER: Match backend enum values EXACTLY
export enum AppointmentStatus {
  SCHEDULED = 'Scheduled',      // ✅ Must match backend: "Scheduled"
  CONFIRMED = 'Confirmed',      // ✅ Added missing value
  IN_PROGRESS = 'In Progress',  // ✅ Added missing value
  COMPLETED = 'Completed',      // ✅ Must match backend: "Completed"
  CANCELLED = 'Cancelled',      // ✅ Must match backend: "Cancelled"
  NO_SHOW = 'No Show',          // ✅ Must match backend: "No Show"
  RESCHEDULED = 'Rescheduled'   // ✅ Added missing value
}

// ✅ FIXED FOREVER: Match backend enum values EXACTLY
export enum AppointmentType {
  IN_PERSON = 'In-Person',              // ✅ Must match backend: "In-Person"
  TELECONSULTATION = 'Teleconsultation', // ✅ Must match backend: "Teleconsultation"
  FOLLOW_UP = 'Follow-up',               // ✅ Must match backend: "Follow-up"
  EMERGENCY = 'Emergency',               // ✅ Must match backend: "Emergency"
  ROUTINE_CHECKUP = 'Routine Checkup',   // ✅ Added missing value
  PHYSIOTHERAPY = 'Physiotherapy Session', // ✅ Added missing value
  LAB_TEST = 'Lab Test',                 // ✅ Added missing value
  IMAGING = 'Imaging/Scan'               // ✅ Added missing value
}

export interface Appointment {
  id: number;
  patient_id: number;
  patient_name: string;
  doctor_id: number;
  doctor_name: string;
  appointment_type: string;
  scheduled_date: string;
  duration_minutes: number;
  status: string;
  reason: string;
  notes?: string;
  consultation_notes?: string;
  created_at: string;
  created_by_user_id?: number;  // ✅ Track who created the appointment
  coordinator_id?: number;  // ✅ Track coordinator assignments
}

export interface AppointmentCreate {
  patient_id: number;
  doctor_id: number;
  appointment_type: AppointmentType;
  scheduled_date: string;
  duration_minutes?: number;
  reason: string;
  notes?: string;
}

export interface AppointmentUpdate {
  scheduled_date?: string;
  status?: AppointmentStatus;
  notes?: string;
  consultation_notes?: string;
}

export interface AppointmentNotesUpdate {
  notes: string;
}

export interface AppointmentFilters {
  status?: AppointmentStatus;
  appointment_type?: AppointmentType;
  start_date?: string;
  end_date?: string;
}

// ==================== API Functions ====================

/**
 * Get all appointments with optional filters
 */
export async function getAppointments(
  filters?: AppointmentFilters
): Promise<Appointment[]> {
  const response = await apiClient.get('/appointments', filters);
  return response;
}

/**
 * Get a specific appointment
 */
export async function getAppointment(id: number): Promise<Appointment> {
  const response = await apiClient.get(`/appointments/${id}`);
  return response;
}

/**
 * Create a new appointment
 */
export async function createAppointment(
  data: AppointmentCreate
): Promise<Appointment> {
  const response = await apiClient.post('/appointments', data);
  return response;
}

/**
 * Update an appointment
 */
export async function updateAppointment(
  id: number,
  data: AppointmentUpdate
): Promise<Appointment> {
  const response = await apiClient.patch(`/appointments/${id}`, data);
  return response;
}

/**
 * Cancel an appointment
 */
export async function cancelAppointment(id: number): Promise<void> {
  await apiClient.delete(`/appointments/${id}`);
}

/**
 * Update appointment notes
 * Used by: AppointmentsScreen component
 */
export async function updateAppointmentNotes(
  appointmentId: number,
  notes: string
): Promise<{
  status: string;
  message: string;
  appointment_id: number;
  notes: string;
}> {
  const response = await apiClient.put(`/appointments/${appointmentId}/notes`, { notes });
  return response;
}

// Export object for consistency
export const appointmentsApi = {
  getAppointments,
  getAppointment,
  createAppointment,
  updateAppointment,
  cancelAppointment,
  updateAppointmentNotes,
};